/**
 * ProgressBar — shadcn Progress 어댑터. 기존 value/size/color/showLabel/animated API 유지.
 *
 * value는 0-100 범위로 이미 정규화되어 있으므로 그대로 shadcn Progress에 전달.
 * color prop → Tailwind 색 클래스로 매핑 (progress indicator 색 오버라이드).
 * size prop → 바 높이 클래스로 매핑 (sm: h-1, md: h-2 기본).
 * animated → animate-pulse 적용.
 * showLabel → 퍼센트 레이블 우측에 표시.
 */

import { Progress } from '@/components/ui/progress'
import { cn } from './cn'

interface ProgressBarProps {
  value: number
  size?: 'sm' | 'md'
  color?: 'primary' | 'success' | 'warning' | 'danger'
  showLabel?: boolean
  animated?: boolean
  className?: string
}

// indicator 색상 오버라이드 클래스 (shadcn Progress의 Indicator에 직접 색 적용 불가 → 루트 CSS 변수로 우회)
const COLOR_CLASS: Record<NonNullable<ProgressBarProps['color']>, string> = {
  primary: '',
  success: '[&>[data-slot=progress-indicator]]:bg-green-500',
  warning: '[&>[data-slot=progress-indicator]]:bg-amber-500',
  danger: '[&>[data-slot=progress-indicator]]:bg-red-500',
}

export function ProgressBar({
  value,
  size = 'md',
  color = 'primary',
  showLabel = false,
  animated = false,
  className,
}: ProgressBarProps) {
  const clamped = Math.min(100, Math.max(0, value))

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <Progress
        value={clamped}
        className={cn(
          size === 'sm' ? 'h-1' : 'h-2',
          animated && 'animate-pulse',
          COLOR_CLASS[color],
          'flex-1',
        )}
      />
      {showLabel && (
        <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
          {clamped}%
        </span>
      )}
    </div>
  )
}
